import React from 'react';
import { cn } from '../../../lib/cn';

/**
 * Skeleton and its layouts, the loading placeholders.
 *
 * Source selectors: `system.css` `.dsk-skeleton` + `--text`, `--title`,
 * `--pill`, `--avatar`, `--thumb`, `--tile`, `--btn`; `.dsk-skeleton-lines`;
 * `.dsk-skeleton-row` + `--compact`, `__main`, `__end`; `.dsk-skeleton-card`.
 *
 * Doctrine enforced:
 * - A skeleton mirrors the SHAPE of what is arriving. It never spins, never
 *   shows a percentage and never says "Loading", because the layout itself is
 *   the message. The app currently hand-rolls thirteen of these and one table
 *   falls back to the literal string "Loading...".
 * - Tone is mixed from `--text-primary` at 8%, so one recipe holds in both
 *   themes. A fixed grey would glow on a near-black ground.
 * - The shimmer is a sweeping band, not a pulsing opacity: a pulse reads as a
 *   broken element, a sweep reads as work in progress. Reduced motion keeps the
 *   block and drops the sweep, and nothing is lost because the shape carried it.
 * - Skeletons are decorative, so every one of these is `aria-hidden`. Put
 *   `aria-busy="true"` on the region that is loading and announce the result
 *   there; a screen reader should never be read a wall of empty bars.
 * - `.dsk-skeleton-row` lands on the same 48px row height and hairline as
 *   `.dsk-table`, so the real rows replace it without a reflow. Inside a real
 *   `<table>` put `.dsk-skeleton .dsk-skeleton--text` in the cells instead,
 *   because `.dsk-table td` already owns the row height.
 *
 * Widths are the caller's business, which is why `width` is a real prop and
 * lands as an inline style. That is geometry, not theme: no colour, no token
 * and nothing that a theme flip needs to change.
 */
const SHAPES = {
  text: 'dsk-skeleton--text',
  title: 'dsk-skeleton--title',
  pill: 'dsk-skeleton--pill',
  avatar: 'dsk-skeleton--avatar',
  thumb: 'dsk-skeleton--thumb',
  tile: 'dsk-skeleton--tile',
  btn: 'dsk-skeleton--btn',
};

export const Skeleton = React.forwardRef(function Skeleton(
  { shape = 'text', width, style, className, ...rest },
  ref,
) {
  return (
    <span
      ref={ref}
      aria-hidden="true"
      className={cn('dsk-skeleton', SHAPES[shape] || null, className)}
      style={width !== undefined ? { width, ...style } : style}
      {...rest}
    />
  );
});
Skeleton.displayName = 'Skeleton';

/**
 * SkeletonLines, `.dsk-skeleton-lines`. A paragraph of bars whose last line is
 * short, which is what makes the stack read as prose rather than a barcode.
 */
export const SkeletonLines = React.forwardRef(function SkeletonLines(
  { lines = 3, children, className, ...rest },
  ref,
) {
  const rows = [];
  for (let i = 0; i < lines; i += 1) rows.push(i);

  return (
    <div ref={ref} aria-hidden="true" className={cn('dsk-skeleton-lines', className)} {...rest}>
      {children || rows.map((i) => <Skeleton key={i} shape="text" />)}
    </div>
  );
});
SkeletonLines.displayName = 'SkeletonLines';

/** SkeletonRow, `.dsk-skeleton-row` (+ `--compact`), `__main`, `__end`. */
export const SkeletonRow = React.forwardRef(function SkeletonRow(
  { compact = false, thumb = true, end = true, children, className, ...rest },
  ref,
) {
  return (
    <div
      ref={ref}
      aria-hidden="true"
      className={cn('dsk-skeleton-row', compact && 'dsk-skeleton-row--compact', className)}
      {...rest}
    >
      {children || (
        <>
          {thumb ? <Skeleton shape="thumb" /> : null}
          <div className="dsk-skeleton-row__main">
            <Skeleton shape="text" width="46%" />
            <Skeleton shape="text" width="28%" />
          </div>
          {end ? (
            <div className="dsk-skeleton-row__end">
              <Skeleton shape="text" width="64px" />
              <Skeleton shape="text" width="40px" />
            </div>
          ) : null}
        </>
      )}
    </div>
  );
});
SkeletonRow.displayName = 'SkeletonRow';

/**
 * SkeletonCard, `.dsk-skeleton-card`. Same anatomy as `.dsk-listing-card`:
 * tile, kicker, title, price. Drops straight into a `.dsk-card-grid`.
 */
export const SkeletonCard = React.forwardRef(function SkeletonCard(
  { children, className, ...rest },
  ref,
) {
  return (
    <div ref={ref} aria-hidden="true" className={cn('dsk-skeleton-card', className)} {...rest}>
      {children || (
        <>
          <Skeleton shape="tile" />
          <Skeleton shape="text" width="42%" />
          <Skeleton shape="title" width="72%" />
          <Skeleton shape="text" width="34%" />
        </>
      )}
    </div>
  );
});
SkeletonCard.displayName = 'SkeletonCard';

export default Skeleton;
